import { useCallback, useState } from "react";
import { useFlash } from "@ziolo/ui";
import { ApiRequestError, errorMessage } from "../lib/apiClient.js";

export interface FormSubmit {
  busy: boolean;
  /** Field-level messages from the last rejected submit, keyed by field name. */
  fields: Record<string, string>;
  setFields: (fields: Record<string, string>) => void;
  /** Runs `action`, flashing either `success` or the API's own error message. */
  submit: (action: () => Promise<void>, success: string) => Promise<boolean>;
}

/**
 * The submit half of every form on the hub: a busy flag, the server's field errors,
 * and one toast either way. Written once because the four forms here would otherwise
 * each grow their own slightly different version.
 */
export function useFormSubmit(): FormSubmit {
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const { push } = useFlash();

  const submit = useCallback(
    async (action: () => Promise<void>, success: string) => {
      setBusy(true);
      setFields({});

      try {
        await action();
        push(success, "success");
        return true;
      } catch (cause) {
        if (cause instanceof ApiRequestError && cause.fields) {
          setFields(cause.fields);
        }
        push(errorMessage(cause), "error");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [push],
  );

  return { busy, fields, setFields, submit };
}
